import { useState } from 'react'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Clock, Mail } from 'lucide-react'
import { WaitlistInquiryForm } from './WaitlistInquiryForm'
import { useAuth } from '@/contexts/AuthContext'
import { toast } from 'sonner'

/**
 * Full-screen modal shown to waitlisted users
 * Cannot be dismissed - users can only submit inquiry or sign out
 */
export function WaitlistModal() {
  const [showInquiryForm, setShowInquiryForm] = useState(false)
  const { signOut } = useAuth()

  const handleSignOut = async () => {
    try {
      await signOut()
    } catch (error) {
      toast.error('Failed to sign out')
    }
  }

  return (
    <>
      <Dialog open={true} onOpenChange={() => {}}>
        <DialogContent
          className="max-w-2xl [&>button]:hidden"
        >
          <DialogHeader className="text-center space-y-4">
            <div className="mx-auto w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
              <Clock className="w-8 h-8 text-primary" />
            </div>
            <DialogTitle className="text-2xl">You're on the waitlist!</DialogTitle>
            <DialogDescription asChild>
              <div className="text-base space-y-4">
                <span className="block">
                  Thank you for your interest in <span className="font-semibold">AionTrack</span>!
                </span>
                <span className="block">
                  AionTrack is a powerful editorial task management platform designed for film and TV
                  post production teams. We help you track stock, clips, manage VFX shots, collaborate with
                  your team, and streamline your entire editorial workflow.
                </span>
                <span className="block">
                  We're currently in beta and reviewing accounts carefully to ensure the best experience
                  for our early users.
                </span>
              </div>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 pt-4">
            <div className="bg-muted/50 rounded-lg p-4 space-y-3">
              <div className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-muted-foreground mt-0.5" />
                <div className="flex-1 space-y-1">
                  <p className="text-sm font-medium">We'll notify you by email</p>
                  <p className="text-sm text-muted-foreground">
                    You'll receive an email as soon as your account is activated.
                    This usually happens within 1-2 business days.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-3">
              <Button
                onClick={() => setShowInquiryForm(true)}
                variant="default"
                className="w-full"
              >
                Have Questions or Need Faster Access?
              </Button>

              <Button
                onClick={handleSignOut}
                variant="outline"
                className="w-full"
              >
                Sign Out
              </Button>
            </div>

            <p className="text-xs text-center text-muted-foreground">
              Questions? Contact us at support@aiontrack.app
            </p>
          </div>
        </DialogContent>
      </Dialog>

      <WaitlistInquiryForm
        open={showInquiryForm}
        onOpenChange={setShowInquiryForm}
      />
    </>
  )
}
